import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Shield, Server, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExternalApps() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Backend Integration</h1>
          <p className="text-muted-foreground">Internal service configuration for User Backend communication</p>
        </div>
      </div>

      {/* Architecture Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Three-Layer Architecture
          </CardTitle>
          <CardDescription>
            Secure backend-to-backend communication model
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-100 p-4 rounded-lg">
            <div className="flex flex-col items-center gap-2 text-sm font-mono">
              <div className="bg-blue-100 px-4 py-2 rounded border border-blue-300">
                User Frontend (UI Only)
              </div>
              <div className="text-gray-400">↓</div>
              <div className="bg-green-100 px-4 py-2 rounded border border-green-300">
                User Backend (Auth, Credits, Jobs)
              </div>
              <div className="text-gray-400">↓ X-Internal-Service-Key</div>
              <div className="bg-purple-100 px-4 py-2 rounded border border-purple-300">
                Admin Backend (AI Integrations, Routing, Execution)
              </div>
            </div>
          </div>
          
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Security Model:</strong> Admin Backend APIs are NOT accessible from frontend or browsers. 
              Only User Backend can call Admin Backend using the internal service key.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Internal API Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="w-5 h-5" />
            Internal API Endpoints
          </CardTitle>
          <CardDescription>
            Endpoints available to User Backend via X-Internal-Service-Key header
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Endpoint</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-mono text-sm">/api/v1/internal/execute</TableCell>
                <TableCell><Badge>POST</Badge></TableCell>
                <TableCell>Execute AI tasks (video, image, audio generation)</TableCell>
                <TableCell><Badge variant="outline" className="text-green-600"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge></TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-mono text-sm">/api/v1/internal/health</TableCell>
                <TableCell><Badge variant="secondary">GET</Badge></TableCell>
                <TableCell>Health check for service connectivity</TableCell>
                <TableCell><Badge variant="outline" className="text-green-600"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge></TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-mono text-sm">/api/v1/internal/capabilities</TableCell>
                <TableCell><Badge variant="secondary">GET</Badge></TableCell>
                <TableCell>List available AI capabilities</TableCell>
                <TableCell><Badge variant="outline" className="text-green-600"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge></TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-mono text-sm">/api/v1/internal/status</TableCell>
                <TableCell><Badge variant="secondary">GET</Badge></TableCell>
                <TableCell>Get system status and disabled task types</TableCell>
                <TableCell><Badge variant="outline" className="text-green-600"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Security Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Security Rules
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h3 className="font-semibold text-green-600 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Allowed
              </h3>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>✓ User Backend → Admin Backend (with service key)</li>
                <li>✓ Backend-to-backend communication</li>
                <li>✓ Internal service key authentication</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-red-600 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Forbidden
              </h3>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>✗ Frontend → Admin Backend (direct calls)</li>
                <li>✗ Browser → Admin Backend (any requests)</li>
                <li>✗ User identity in Admin Backend requests</li>
                <li>✗ Provider/model IDs in responses</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Service Key Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Service Key Configuration</CardTitle>
          <CardDescription>
            The internal service key is configured in the backend .env file
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 text-slate-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
            <p className="text-slate-400"># User Backend .env configuration</p>
            <p>ADMIN_BACKEND_URL={API_URL}</p>
            <p>ADMIN_SERVICE_KEY=admin_svc_xxxxx...xxxxx</p>
            <p></p>
            <p className="text-slate-400"># Example call from User Backend</p>
            <p>response = requests.post(</p>
            <p className="pl-4">f"&#123;ADMIN_BACKEND_URL&#125;/api/v1/internal/execute",</p>
            <p className="pl-4">headers=&#123;"X-Internal-Service-Key": ADMIN_SERVICE_KEY&#125;,</p>
            <p className="pl-4">json=&#123;"task_type": "video_generation", "payload": &#123;...&#125;&#125;</p>
            <p>)</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
